import { Card, CardContent } from '@components/card';
import { BaseLayout } from '@layouts/base';
import { createFileRoute, redirect, useNavigate, useParams } from '@tanstack/react-router';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { ApiKeyForm } from '@components/forms/apiKeys/index';
import { useGetPermissionGroups } from '@services/acquirer/apiKeys/permissionGroups';
import { useApiKeyForm } from '@hooks/forms/apiKeys';
import { useGetApiKey } from '@services/staff/acquirer/apiKeys/get';
import { Loader } from '@components/loader';
import { Page404 } from '@pages/404';
import { useUpdateApiKey } from '@services/staff/acquirer/apiKeys/update';

const EditApiKey = () => {
  const { t } = useTranslation('app');
  const { localId, acquirerId, id } = useParams({
    from: '/u/$localId/_staff/staff/acquirers/$acquirerId/api-keys/$id/edit',
  });
  const navigate = useNavigate();
  const { data: permissions } = useGetPermissionGroups();
  const { data, isPending } = useGetApiKey({ acquirerId, id });

  const requestHandler = useUpdateApiKey({
    id,
    acquirerId,
    callback: () => {
      toast.success(t('toast.apiKeys.updateSuccess'));
      navigate({
        to: '/u/$localId/staff/acquirers/$acquirerId/api-keys/$id',
        params: { localId, acquirerId, id },
        replace: true,
      }).catch((err) => {
        console.error(err);
      });
    },
  });

  const form = useApiKeyForm({
    apiKey: data,
    requestHandler,
  });

  if (isPending) return <Loader />;
  if (!data) return <Page404 />;

  return (
    <BaseLayout isChild title={t('pages.apiKeys.edit')}>
      <Card>
        <CardContent className="p-6">
          {permissions && (
            <ApiKeyForm
              formObj={form}
              permissions={permissions}
              buttonText={t('form.submit.apiKeyUpdate')}
            />
          )}
        </CardContent>
      </Card>
    </BaseLayout>
  );
};

export const Route = createFileRoute(
  '/u/$localId/_staff/staff/acquirers/$acquirerId/api-keys/$id/edit',
)({
  beforeLoad: ({ context }) => {
    if (!context.userHasPermissions(['api_keys_read', 'api_keys_update'])) {
      throw redirect({
        to: `/u/$localId/${context.userType}/forbidden`,
      });
    }
  },
  component: EditApiKey,
});
